import HourMinuteInput, { parseTimeStringToMinutes } from '@components/_SiteWide/Form/HourMinuteInput'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { getShowNetworkTimezone } from '@utils/general'
import { DatePicker, Form, type FormInstance } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { type Moment } from 'moment-timezone'
import type React from 'react'
import { useState } from 'react'

function endOfToday(timezone: Intl.DateTimeFormatOptions['timeZone']) {
  return getShowNetworkTimezone()
    ? dayjs().tz(timezone).add(1, 'days').startOf('day')
    : dayjs().add(1, 'days').startOf('day')
}

const disabledPastDates = (current: Moment | Dayjs, timeZone: Intl.DateTimeFormatOptions['timeZone']) => {
  // Disable dates before today
  return getShowNetworkTimezone({ timeZone })
    ? current < dayjs().tz(timeZone).startOf('day')
    : current < dayjs().startOf('day')
}
const validateDateNotInPast = (_, value, timeZone: Intl.DateTimeFormatOptions['timeZone']) => {
  if (value?.isBefore(getShowNetworkTimezone({ timeZone }) ? dayjs().tz(timeZone) : dayjs())) {
    return Promise.reject(new Error('Selection cannot be in the past'))
  }
  return Promise.resolve()
}

type TemporaryOverrideFormProps = {
  metadataComponent: React.ReactNode
  dwellOverride: boolean
  form: FormInstance
  handleOverride: (values) => void
  handleDelete: () => void
  closeForm: () => void
  timezone: Intl.DateTimeFormatOptions['timeZone']
}

export function TemporaryOverrideForm({
  metadataComponent,
  dwellOverride,
  form,
  handleOverride,
  handleDelete,
  closeForm,
  timezone,
}: TemporaryOverrideFormProps) {
  const [selectedButton, setSelectedButton] = useState('EOD')
  const handleButtonClick = (button: string) => {
    setSelectedButton(button)
    if (button === 'EOD') {
      form.setFieldsValue({
        date: getShowNetworkTimezone({ timeZone: timezone }) ? dayjs().tz(timezone).endOf('day') : dayjs().endOf('day'),
      })
    } else if (button === 'EOW') {
      form.setFieldsValue({
        date: getShowNetworkTimezone({ timeZone: timezone })
          ? dayjs().tz(timezone).endOf('week').endOf('day')
          : dayjs().endOf('week').endOf('day'),
      })
    } else if (button === 'Custom') {
      form.setFieldsValue({
        date: null,
      })
    }
  }

  const handlePickerManualChange = () => {
    setSelectedButton('Custom')
  }

  const onDelete = () => {
    handleDelete()
    closeForm()
  }

  const onFinish = (values) => {
    handleOverride(values)
    closeForm()
  }

  return (
    <Form form={form} onFinish={onFinish}>
      <Vertical style={{ gap: '.5em' }}>
        <Vertical style={{ gap: '.5em', borderBottom: '2px solid var(--gray-100)' }} className='pb-1'>
          {metadataComponent}
          <Texto weight='bold'>Dwell Time Override</Texto>
          <Form.Item
            name='overrideTime'
            rules={[
              { required: true, message: 'Override time is required' },
              {
                validator: (_, value) => {
                  const parsed = parseTimeStringToMinutes(value) / 60 || value
                  if (parsed > 0 && parsed <= 12) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Must be between 0 and 12 hours.'))
                },
              },
            ]}
          >
            <HourMinuteInput />
          </Form.Item>
          <Texto weight='bold'>Override Expires</Texto>
          <Horizontal>
            <GraviButton buttonText='EOD' theme2={selectedButton === 'EOD'} onClick={() => handleButtonClick('EOD')} />
            <GraviButton buttonText='EOW' theme2={selectedButton === 'EOW'} onClick={() => handleButtonClick('EOW')} />
            <GraviButton
              buttonText='Custom'
              theme2={selectedButton === 'Custom'}
              onClick={() => handleButtonClick('Custom')}
            />
          </Horizontal>

          <Horizontal>
            <Form.Item
              name='date'
              rules={[
                { required: true, message: 'Date is required' },
                { validator: (_, value) => validateDateNotInPast(_, value, timezone) },
              ]}
              initialValue={endOfToday(timezone)}
            >
              <DatePicker
                placeholder='Date'
                format='MM/DD/YYYY hh:mm A'
                onClick={handlePickerManualChange}
                disabledDate={(current) => disabledPastDates(current, timezone)}
                showTime={{ format: 'hh:mm A' }}
              />
            </Form.Item>
          </Horizontal>
        </Vertical>
        <Horizontal justifyContent='flex-end' style={{ gap: '1em' }}>
          <GraviButton appearance='outlined' buttonText='Delete' error disabled={!dwellOverride} onClick={onDelete} />
          <GraviButton buttonText='Update' theme1 onClick={form.submit} />
        </Horizontal>
      </Vertical>
    </Form>
  )
}
